import type { IntervieweeCard, IntervieweeDetail, MediaPreview, VillageViewModel } from "../../domain/contracts";
import { IntervieweeTimeline } from "./components/IntervieweeTimeline";
import { IntervieweeMediaOverlay } from "./components/IntervieweeMediaOverlay";
import { IntervieweeIncidentWitnessSection } from "../incidents/components/IntervieweeIncidentWitnessSection";
import { IncidentDetailsCarousel } from "../incidents/components/IncidentDetailsCarousel";
import { useIntervieweeSectionViewModel } from "./hooks/useIntervieweeSectionViewModel";

type IntervieweeSectionProps = {
  village: VillageViewModel | null;
  selectedInterviewee: IntervieweeCard | null;
  intervieweeDetail: IntervieweeDetail | null;
  mediaPreviews: MediaPreview[];
  isLoadingDetail: boolean;
  returnTo: "map" | "villagesList" | "intervieweesList" | "village";
  onBack: () => void;
};

export function IntervieweeSection({
  village,
  selectedInterviewee,
  intervieweeDetail,
  mediaPreviews,
  isLoadingDetail,
  returnTo,
  onBack,
}: IntervieweeSectionProps) {
  const {
    witnessHeading,
    backLabel,
    documentEvidenceLabel,
    displacementReasonLabel,
    violationItems,
    activeViolationText,
    setActiveViolation,
    timelineItems,
    hasIncidentDetails,
    witnessItems,
    incidentCards,
    evidenceGroups,
    isDownloadingAll,
    handleDownloadAll,
    hasDownloadItems,
    activePreview,
    openPreview,
    closePreview,
  } = useIntervieweeSectionViewModel({
    village,
    selectedInterviewee,
    intervieweeDetail,
    mediaPreviews,
    returnTo,
  });

  const backButton = (
    <button type="button" className="nav-back-button" onClick={onBack}>
      <span className="nav-back-button__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" role="presentation">
          <path d="M14.4 6.4L9 11.8l5.4 5.4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>{backLabel}</span>
    </button>
  );

  const reportedViolationsCard = (
    <article className="box summary-violations fade-in">
      <div className="card-accent" aria-hidden="true" />
      <h3>Reported Human Rights Violations</h3>
      {violationItems.length > 0 ? (
        <div className="summary-violations__icons">
          {violationItems.map((item) => (
            <button
              key={item.tag}
              type="button"
              className="violation-icon-button"
              onMouseEnter={() => setActiveViolation(item.tag)}
              onMouseLeave={() => setActiveViolation(null)}
              onFocus={() => setActiveViolation(item.tag)}
              onBlur={() => setActiveViolation(null)}
              aria-label={item.label}
            >
              <img src={item.icon} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      ) : null}
      <div className="summary-violations__text" aria-live="polite">
        {activeViolationText}
      </div>
    </article>
  );

  const timelineCard = (
    <article className="box timeline-card fade-in">
      <IntervieweeTimeline items={timelineItems} />
    </article>
  );

  return (
    <section className="profile-view" aria-labelledby="interviewee-profile-title">
      <div className="profile-view__inner">
        {backButton}
        <div className="frame frame-interviewee profile-grid">
          <article className="box interviewee-hero profile-grid__span fade-in">
            <div className="card-accent" aria-hidden="true" />
            <div className="interviewee-portrait">
              <img src="/profile.png" alt="Interviewee image placeholder" loading="lazy" />
            </div>
            <div className="interviewee-hero__meta">
              <h3>{witnessHeading}</h3>
              <div className="interviewee-hero__meta-list">
                <div className="interviewee-hero__meta-item">
                  <span className="meta-icon" aria-hidden="true">
                    <img src="/village-icon-no-data.png" alt="" />
                  </span>
                  <span className="interviewee-hero__meta-label">Village:</span>
                  <span className="interviewee-hero__meta-value">
                    {village?.title ?? "-"}
                    {village?.township ? `, ${village.township}` : ""}
                  </span>
                </div>
                <div className="interviewee-hero__meta-item">
                  <span className="meta-icon" aria-hidden="true">
                    <img src="/document-icon.png" alt="" />
                  </span>
                  <span className="interviewee-hero__meta-label">Village of Origin Verification:</span>
                  <span className="interviewee-hero__meta-value">{documentEvidenceLabel}</span>
                </div>
                <div className="interviewee-hero__meta-item">
                  <span className="meta-icon" aria-hidden="true">
                    <img src="/displacement-reason.png" alt="" />
                  </span>
                  <span className="interviewee-hero__meta-label">Reason for Displacement:</span>
                  <span className="interviewee-hero__meta-value">{displacementReasonLabel}</span>
                </div>
              </div>
            </div>
            <div className="interviewee-hero__aside">
              <div>
                <p className="small-meta">ID</p>
                <p className="small-meta strong">{selectedInterviewee?.responseId ?? "-"}</p>
              </div>
              <div>
                <p className="small-meta">Updated</p>
                <p className="small-meta strong">{selectedInterviewee?.updatedAt?.slice(0, 10) ?? "-"}</p>
              </div>
            </div>
          </article>

          {hasIncidentDetails ? (
            <div className="interviewee-incident-layout profile-grid__span">
              <div className="interviewee-incident-top-row">
                <div className="interviewee-incident-panel">
                  {reportedViolationsCard}
                </div>
                <div className="interviewee-incident-panel">
                  <IntervieweeIncidentWitnessSection items={witnessItems} />
                </div>
              </div>

              <div className="interviewee-incident-bottom-row">
                <div className="interviewee-incident-panel">
                  {timelineCard}
                </div>
                <div className="interviewee-incident-panel">
                  <IncidentDetailsCarousel cards={incidentCards} />
                </div>
              </div>
            </div>
          ) : (
            <>
              {reportedViolationsCard}
              {timelineCard}
            </>
          )}

          <article className="box evidence-documents profile-grid__span fade-in">
            <div className="card-accent" aria-hidden="true" />
            <div className="box-title-row">
              <h3>Survivor Village and Atrocity Evidence</h3>
              <button
                type="button"
                className="secondary-button is-inline"
                onClick={handleDownloadAll}
                disabled={!hasDownloadItems || isDownloadingAll}
              >
                {isDownloadingAll ? "Preparing zip..." : "Download all"}
              </button>
            </div>
            {!selectedInterviewee ? (
              <p className="small-paragraph">Select an interviewee card to load linked documentation.</p>
            ) : isLoadingDetail ? (
              <p className="small-paragraph">Loading interviewee documentation...</p>
            ) : evidenceGroups.length > 0 ? (
              <div className="evidence-doc-group-stack">
                {evidenceGroups.map((group) => (
                  <div key={group.key} className="evidence-doc-group">
                    <p className="small-meta">{group.title}</p>
                    <div className="evidence-doc-strip">
                      {group.items.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          className="evidence-doc-card"
                          onClick={() => openPreview(item.overlayItem)}
                          disabled={!item.overlayItem}
                          aria-label={`Open ${item.previewLabel} preview`}
                        >
                          <div className="evidence-doc-card__image">
                            <img
                              src={item.previewSource}
                              alt={item.previewLabel}
                              loading="lazy"
                            />
                          </div>
                          <div className="evidence-doc-card__meta">
                            <strong title={item.previewLabel}>{item.previewLabel}</strong>
                            <span>{item.mediaLine}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="small-paragraph">No published evidence is linked to this interviewee yet.</p>
            )}
          </article>
        </div>

        {activePreview ? (
          <IntervieweeMediaOverlay activePreview={activePreview} onClose={closePreview} />
        ) : null}

        {backButton}
      </div>
    </section>
  );
}
